// Pure helpers for the "Where" + "Access ends on" part of a role assignment
// (compensation/payroll authorization Phase 2/5a). Kept out of the components
// so the validation rules are tested without rendering.
//
// Owner decision O-1 (multi-entity workforce, 2026-10-23): "all companies,
// including ones added later" is reserved for the owner role. The "all" choice
// here therefore means EVERY CURRENT COMPANY BY NAME: it is sent as
// SELECTED_ENTITIES with today's company ids, so a company created next year
// never appears in anyone's reach without someone granting it. A legacy
// assignment that still says ALL_AUTHORIZED_ENTITIES is left as it is when only
// its end date is edited (converting it is the owner's all-companies step).
//
// Three choices, not two: "No company limit" (the default) sends NO entity
// scope, which keeps the assignment exactly as it behaved before entity-scoped
// assignments existed. Making every new assignment "All authorized companies"
// by default would silently switch the whole company onto entity scoping (and
// require the assigning admin to reach every company themselves).
import type { Assignment, AssignmentScopeInput, EntityScope } from '@/services/rbacService';

export type WhereChoice = 'unscoped' | 'all' | 'selected';

export interface ScopeForm {
  where: WhereChoice;
  /** Legal entity ids, used only when where = 'selected'. */
  entityIds: string[];
  /** 'YYYY-MM-DD' (last day of access) or '' for no end date. */
  endsOn: string;
}

export const EMPTY_SCOPE_FORM: ScopeForm = { where: 'unscoped', entityIds: [], endsOn: '' };

export interface ScopeFormErrors {
  entityIds?: string;
  endsOn?: string;
}

/** End of the chosen civil day in the browser's zone — access lasts through it. */
export function endOfDayIso(day: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Local 'YYYY-MM-DD' of an ISO timestamp (for prefilling the date input). */
export function isoToDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function validateScopeForm(
  form: ScopeForm,
  now: Date = new Date(),
  /** Today's company ids; when given and empty, "every current company" cannot be named. */
  allCompanyIds?: string[],
): ScopeFormErrors {
  const errors: ScopeFormErrors = {};
  if (form.where === 'selected' && form.entityIds.length === 0) {
    errors.entityIds = 'Choose at least one company.';
  }
  if (form.where === 'all' && allCompanyIds && allCompanyIds.length === 0) {
    errors.entityIds = 'Your companies could not be loaded, so they cannot be named. Try again.';
  }
  if (form.endsOn) {
    const iso = endOfDayIso(form.endsOn);
    if (!iso) errors.endsOn = 'Enter a valid date.';
    else if (new Date(iso).getTime() <= now.getTime()) errors.endsOn = 'The end date must be in the future.';
  }
  return errors;
}

export const hasErrors = (e: ScopeFormErrors) => !!(e.entityIds || e.endsOn);

const SCOPE_OF: Record<WhereChoice, EntityScope | null> = {
  unscoped: null,
  all: 'SELECTED_ENTITIES', // O-1: every CURRENT company, by name
  selected: 'SELECTED_ENTITIES',
};

const idsFor = (form: ScopeForm, allCompanyIds: string[]) =>
  form.where === 'all' ? [...allCompanyIds] : [...form.entityIds];

/**
 * Body for POST rbac/assignments. An untouched default form adds nothing, so
 * the request is byte-identical to the pre-scope one.
 */
export function toCreatePayload(form: ScopeForm, allCompanyIds: string[] = []): AssignmentScopeInput {
  const out: AssignmentScopeInput = {};
  const scope = SCOPE_OF[form.where];
  if (scope) out.entityScope = scope;
  if (scope === 'SELECTED_ENTITIES') out.legalEntityIds = idsFor(form, allCompanyIds);
  const iso = form.endsOn ? endOfDayIso(form.endsOn) : null;
  if (iso) out.expiresAt = iso;
  return out;
}

/**
 * Body for PATCH rbac/assignments/:id — always the full resulting state. A
 * legacy ALL_AUTHORIZED_ENTITIES assignment whose "where" is untouched stays as
 * it is (only its end date changes).
 */
export function toUpdatePayload(
  form: ScopeForm,
  allCompanyIds: string[] = [],
  previous?: EntityScope | null,
): AssignmentScopeInput {
  const expiresAt = form.endsOn ? endOfDayIso(form.endsOn) : null;
  if (form.where === 'all' && previous === 'ALL_AUTHORIZED_ENTITIES') {
    return { entityScope: 'ALL_AUTHORIZED_ENTITIES', expiresAt };
  }
  const scope = SCOPE_OF[form.where];
  return {
    entityScope: scope,
    ...(scope === 'SELECTED_ENTITIES' ? { legalEntityIds: idsFor(form, allCompanyIds) } : {}),
    expiresAt,
  };
}

export function formFromAssignment(a: Assignment): ScopeForm {
  const where: WhereChoice =
    a.entityScope === 'ALL_AUTHORIZED_ENTITIES'
      ? 'all'
      : a.entityScope === 'SELECTED_ENTITIES'
        ? 'selected'
        : 'unscoped';
  return {
    where,
    entityIds: where === 'selected' ? [...(a.legalEntityIds ?? [])] : [],
    endsOn: a.expiresAt ? isoToDay(a.expiresAt) : '',
  };
}

/** Assignment status from its fields (the list endpoint also sends `active`). */
export function assignmentStatus(a: Assignment, now: Date = new Date()): 'active' | 'expired' | 'revoked' {
  if (a.revokedAt) return 'revoked';
  if (a.expiresAt && new Date(a.expiresAt).getTime() <= now.getTime()) return 'expired';
  if (a.active === false) return 'expired';
  return 'active';
}

/**
 * Whether the form changes WHERE the assignment applies (not just when it
 * ends). Only a "where" change can remove companies, so only it is checked
 * for lost access before saving.
 */
export function scopeChanged(a: Assignment, form: ScopeForm): boolean {
  const before = formFromAssignment(a);
  if (before.where !== form.where) return true;
  if (form.where !== 'selected') return false;
  const x = [...before.entityIds].sort();
  const y = [...form.entityIds].sort();
  return x.length !== y.length || x.some((id, i) => id !== y[i]);
}
